import { useHandler } from "@redotech/react-util/hook";
import {
  WaitStep,
  waitStepSeconds,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  getSeconds,
  getTimeValue,
  WaitTimeUnit,
} from "@redotech/redo-flows-common/steps";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import {
  SECONDS_IN_A_DAY,
  SECONDS_IN_A_MINUTE,
  SECONDS_IN_A_YEAR,
} from "@redotech/util/time";
import { DeepPartial } from "@redotech/util/type";
import { memo, useMemo } from "react";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../steps";

const WaitTimePickerWrapper = memo(function WaitTimePickerWrapper(
  props: StepTypeDetailsProps<PartialWithRequiredType<WaitStep>>,
) {
  const { t } = useTranslation();
  const { step, setStep, onClose } = props;

  return (
    <Flex dir="column" gap="3xl">
      <Flex justify="space-between" py="sm">
        <Text fontSize="sm" fontWeight="semibold">
          {t("Wait")}
        </Text>
        {onClose && (
          /* design-allow: preserve the existing inspector close control. */
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XIcon height="16" width="16" />
          </div>
        )}
      </Flex>
      <WaitTimePicker setStep={setStep} step={step} />
    </Flex>
  );
});
export const waitStepType: FrontendStepType<PartialWithRequiredType<WaitStep>> =
  {
    valid: (state) =>
      (waitStepSeconds(state) ?? 0) >= SECONDS_IN_A_MINUTE &&
      (waitStepSeconds(state) ?? 0) <= SECONDS_IN_A_YEAR &&
      !!state.nextId,
    type: "custom",
    customInspector: WaitTimePickerWrapper,
  };

type WaitStepType = { type: StepType.WAIT; id: string } & DeepPartial<
  Omit<WaitStep, "type">
>;

const WaitTimePicker = memo(function WaitTimePicker({
  step,
  setStep,
}: {
  step: WaitStepType;
  setStep: (step: WaitStepType) => void;
}) {
  const { t } = useTranslation();
  const seconds: number | undefined = waitStepSeconds(step);

  const timeUnit = step.timeUnit || WaitTimeUnit.DAYS;
  const timeValue = getTimeValue({ seconds: seconds || 0, timeUnit });
  const items: RedoListItem<WaitTimeUnit>[] = useMemo(
    () => [
      {
        id: WaitTimeUnit.DAYS,
        type: "text",
        text: t("Days"),
        value: WaitTimeUnit.DAYS,
      },
      {
        id: WaitTimeUnit.HOURS,
        type: "text",
        text: t("Hours"),
        value: WaitTimeUnit.HOURS,
      },
      {
        id: WaitTimeUnit.MINUTES,
        type: "text",
        text: t("Minutes"),
        value: WaitTimeUnit.MINUTES,
      },
    ],
    [t],
  );

  const boundSeconds = useHandler((seconds: number) => {
    const boundedValue = Math.max(0, Math.min(SECONDS_IN_A_YEAR, seconds));
    return boundedValue;
  });

  const handleTimeUnitChange = useHandler((timeUnit: WaitTimeUnit) => {
    const seconds = boundSeconds(getSeconds({ timeValue, timeUnit }));

    setStep({
      ...step,
      numSeconds: seconds,
      numDays: Math.floor(seconds / SECONDS_IN_A_DAY),
      timeUnit,
    });
  });

  const setSeconds = useHandler((newSecondsValue: number) => {
    setStep({
      ...step,
      numSeconds: newSecondsValue,
      numDays: Math.floor(newSecondsValue / SECONDS_IN_A_DAY),
    });
  });

  return (
    <Flex dir="column">
      <RedoIncrementDecrement
        fullWidth
        min={1}
        setValue={(n) => {
          const newTimeValue = n || 0;

          setSeconds(
            boundSeconds(getSeconds({ timeValue: newTimeValue, timeUnit })),
          );
        }}
        size="small"
        value={timeValue}
      />
      <RedoSingleSelectDropdownInput<WaitTimeUnit>
        options={items}
        optionSelected={({ item }) => {
          handleTimeUnitChange(item.value);
        }}
        selectedItem={items.find((o) => o.id === timeUnit)}
        size="xs"
      />
    </Flex>
  );
});
